import { useEffect, useRef, useState } from 'react';

import {
    setSharedAuth,
    getSharedAuth,
    logInfo,
} from '../../../utils';

function isPilot () {
    return window.location.origin === 'https://test-pilot-app.ecal.com';
}

function get3dSecureRedirectURL () {
    const paths = {
        local: 'https://localhost:3000/',
        prod: 'https://app.ecal.com/',
        stage: 'https://stage-app.ecal.com/',
        pilot: 'https://test-pilot-app.ecal.com/',
        dev: 'https://test-app.ecal.com/',
    };

    let base = paths[window.env] ? paths[window.env] : paths.prod;
    if (isPilot()) {
        base = paths.pilot;
    }
    if (window.location.hostname === 'localhost') {
        base = paths.local;
    }
    const redirectURL = `${base}3d.secure.redirect.html`;
    logInfo('get3dSecureRedirectURL()', redirectURL);
    return redirectURL;
}

function get3dSecureCallbackURL () {
    // securedCallback doens't need a pilot because it is a service.
    const paths = {
        local: 'http://localhost:3034/v4/billing/secure3DSCallback',
        prod: 'https://svc.ecal.com/v4/billing/secure3DSCallback',
        stage: 'https://stage-svc.ecal.com/v4/billing/secure3DSCallback',
        dev: 'https://test-svc.ecal.com/v4/billing/secure3DSCallback',
    };

    let url = paths[window.env] ? paths[window.env] : paths.prod;
    if (window.location.hostname === 'localhost') {
        url = paths.local;
    }
    logInfo('get3dSecureCallbackURL()', url);
    return url;
}

// 3D Secure Card Ex: 4000000000003220

function useThreeDSecure (props) {
    const {
        store,
        onAuthSuccess = () => {},
        onAuthFailed = () => {},
    } = props;

    const [actionURL, setActionURL] = useState('');

    // The storage listener is registered once (empty deps), so it must not close
    // over a single render's finalizeAuthentication — that would capture stale
    // callbacks (e.g. the checkout's selectedPlan, still null on first mount) and
    // feed them to finalize3DSecure. Route through a ref that always points at the
    // latest finalizeAuthentication instead.
    const finalizeRef = useRef();
    finalizeRef.current = finalizeAuthentication;

    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'ecal-v4-shared-auth' && e.newValue) {
                store.threeDSecureFinalized = false;
                logInfo('Storage Event: 3DS data detected instantly!');
                finalizeRef.current();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        logInfo('CharigyAction Link is updated....');
        if (store.chargifyActionLink) {
            start();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.observed.chargifyActionLink]);

    function start () {
        openActionLink();
        startAuthentication();
        verifySharedAuth();
    }

    function verifySharedAuth () {
        const sharedAuth = getSharedAuth();
        if (sharedAuth.authenticated) {
            finalizeAuthentication();
        } else {
            setTimeout(() => verifySharedAuth(), 1000);
        }
    }

    function startAuthentication () {
        setSharedAuth({
            authenticated: false,
        });
    }

    function finalizeAuthentication () {
        const sharedAuth = getSharedAuth();
        if (sharedAuth.isSuccess) {
            if (!store.threeDSecureFinalized) {
                store.threeDSecureFinalized = true;
                onAuthSuccess();
            }
        } else {
            onAuthFailed(sharedAuth.message);
        }
    }

    function openActionLink () {
        store.threeDSecureFinalized = false;
        const link = `${store.chargifyActionLink}&redirect_url=${get3dSecureRedirectURL()}&callback_url=${get3dSecureCallbackURL()}`;
        logInfo('openActionLink() -> ', link);
        setActionURL(link);
    }

    return {
        actionURL,
    };
}

export default useThreeDSecure;
